import type { OrbitChatUpload } from './OrbitChatService';
import OrbitFilesService from './OrbitFilesService';

const CSV_MIME_TYPES = new Set([
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
]);

const JSON_MIME_TYPES = new Set([
  'application/json',
  'text/json',
  'application/ld+json',
]);

const PDF_MIME_TYPES = new Set([
  'application/pdf',
]);

const EXCEL_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12',
]);

export const ORBIT_CONTEXT_FILE_ACCEPT = [
  '.csv',
  '.json',
  '.pdf',
  '.xlsx',
  '.xls',
  'text/csv',
  'application/csv',
  'application/json',
  'text/json',
  'application/ld+json',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12',
].join(',');

export interface ResolveOrbitContextUploadsOptions {
  clientId?: number;
  conversationId?: string;
}

export interface ResolveOrbitContextUploadsResult {
  uploads: OrbitChatUpload[];
  storedUploadCount: number;
  errors: string[];
}

export interface OrbitAttachmentDescriptor {
  attachmentId: string;
  attachmentLabel: string;
  attachmentType: string;
  attachmentTypeDisplay: string;
  filename: string;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read file'));
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(file);
  });
}

export function isCsvFile(file: File): boolean {
  const filename = String(file?.name || '').toLowerCase();
  const mimeType = String(file?.type || '').toLowerCase();
  if (isExcelFile(file)) {
    return false;
  }
  return filename.endsWith('.csv') || CSV_MIME_TYPES.has(mimeType);
}

export function isJsonFile(file: File): boolean {
  const filename = String(file?.name || '').toLowerCase();
  const mimeType = String(file?.type || '').toLowerCase();
  return filename.endsWith('.json') || JSON_MIME_TYPES.has(mimeType);
}

export function isPdfFile(file: File): boolean {
  const filename = String(file?.name || '').toLowerCase();
  const mimeType = String(file?.type || '').toLowerCase();
  return filename.endsWith('.pdf') || PDF_MIME_TYPES.has(mimeType);
}

export function isExcelFile(file: File): boolean {
  const filename = String(file?.name || '').toLowerCase();
  const mimeType = String(file?.type || '').toLowerCase();
  if (filename.endsWith('.csv')) {
    return false;
  }
  return filename.endsWith('.xlsx') || filename.endsWith('.xls') || EXCEL_MIME_TYPES.has(mimeType);
}

export function isContextUploadFile(file: File): boolean {
  return isCsvFile(file) || isJsonFile(file);
}

export function isStoredContextUploadFile(file: File): boolean {
  return isPdfFile(file) || isExcelFile(file);
}

function classifyAttachmentType(filename: string, contentType: string): 'CSV' | 'JSON' | 'PDF' | 'EXCEL_WORKBOOK' | 'FILE' {
  const normalizedFilename = String(filename || '').toLowerCase();
  const normalizedType = String(contentType || '').toLowerCase();

  if (normalizedFilename.endsWith('.csv') || CSV_MIME_TYPES.has(normalizedType)) {
    return 'CSV';
  }
  if (normalizedFilename.endsWith('.json') || JSON_MIME_TYPES.has(normalizedType)) {
    return 'JSON';
  }
  if (normalizedFilename.endsWith('.pdf') || PDF_MIME_TYPES.has(normalizedType)) {
    return 'PDF';
  }
  if (
    normalizedFilename.endsWith('.xlsx')
    || normalizedFilename.endsWith('.xls')
    || EXCEL_MIME_TYPES.has(normalizedType)
  ) {
    return 'EXCEL_WORKBOOK';
  }
  return 'FILE';
}

function attachmentTypeDisplayName(type: OrbitAttachmentDescriptor['attachmentType']): string {
  if (type === 'CSV') return 'CSV file';
  if (type === 'JSON') return 'JSON file';
  if (type === 'PDF') return 'PDF file';
  if (type === 'EXCEL_WORKBOOK') return 'Excel workbook';
  return 'File';
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36).toUpperCase();
}

function buildAttachmentId(upload: OrbitChatUpload, attachmentType: OrbitAttachmentDescriptor['attachmentType']): string {
  const metadata = upload?.metadata && typeof upload.metadata === 'object'
    ? upload.metadata as Record<string, unknown>
    : {};
  const explicitId = typeof metadata.attachment_id === 'string' && metadata.attachment_id.trim()
    ? metadata.attachment_id.trim()
    : null;
  if (explicitId) {
    return explicitId;
  }

  const orbitFileId = metadata.orbit_file_id;
  if (orbitFileId != null && String(orbitFileId).trim()) {
    return `ATTACHMENT-ORBIT-FILE-${String(orbitFileId).trim()}`;
  }

  const fingerprint = [
    upload.filename,
    upload.content_type,
    String(metadata.size_bytes ?? ''),
    String(metadata.last_modified ?? ''),
  ].join('|');
  return `ATTACHMENT-${attachmentType}-${hashString(fingerprint || upload.filename || 'UPLOAD')}`;
}

export function describeOrbitAttachment(upload: OrbitChatUpload): OrbitAttachmentDescriptor {
  const attachmentType = classifyAttachmentType(upload.filename, upload.content_type);
  const attachmentId = buildAttachmentId(upload, attachmentType);
  const typeDisplay = attachmentTypeDisplayName(attachmentType);
  const metadata = upload?.metadata && typeof upload.metadata === 'object'
    ? upload.metadata as Record<string, unknown>
    : {};
  const explicitLabel = typeof metadata.attachment_label === 'string' && metadata.attachment_label.trim()
    ? metadata.attachment_label.trim()
    : null;

  return {
    attachmentId,
    attachmentLabel: explicitLabel || `${upload.filename} (${typeDisplay})`,
    attachmentType,
    attachmentTypeDisplay: typeDisplay,
    filename: upload.filename,
  };
}

export function normalizeOrbitChatUpload(upload: OrbitChatUpload): OrbitChatUpload {
  const descriptor = describeOrbitAttachment(upload);
  const metadata = upload?.metadata && typeof upload.metadata === 'object'
    ? upload.metadata as Record<string, unknown>
    : {};

  return {
    ...upload,
    metadata: {
      ...metadata,
      attachment_id: descriptor.attachmentId,
      attachment_label: descriptor.attachmentLabel,
      attachment_type_label: descriptor.attachmentTypeDisplay,
      filename: upload.filename,
      content_type: upload.content_type,
    },
  };
}

export async function buildOrbitContextUpload(file: File): Promise<OrbitChatUpload | null> {
  if (!isContextUploadFile(file)) {
    return null;
  }

  const text = await readFileAsText(file);
  if (!text.trim()) {
    return null;
  }

  return normalizeOrbitChatUpload({
    filename: file.name,
    content_type: file.type || (isJsonFile(file) ? 'application/json' : 'text/csv'),
    text,
    metadata: {
      size_bytes: file.size,
      last_modified: file.lastModified,
      source: 'retailorbitv2-web',
    },
  });
}

export async function buildOrbitCsvUpload(file: File): Promise<OrbitChatUpload | null> {
  if (!isCsvFile(file)) {
    return null;
  }

  return buildOrbitContextUpload(file);
}

export async function buildOrbitContextUploads(files: FileList | File[]): Promise<OrbitChatUpload[]> {
  const normalizedFiles = Array.from(files || []);
  const uploads = await Promise.all(normalizedFiles.map((file) => buildOrbitContextUpload(file)));
  return uploads.filter((upload): upload is OrbitChatUpload => Boolean(upload));
}

export async function buildOrbitCsvUploads(files: FileList | File[]): Promise<OrbitChatUpload[]> {
  const normalizedFiles = Array.from(files || []).filter((file) => isCsvFile(file));
  return buildOrbitContextUploads(normalizedFiles);
}

function extractUploadErrorMessage(error: any, fallbackMessage: string): string {
  return error?.response?.data?.message || error?.message || fallbackMessage;
}

function fallbackContentTypeForFile(file: File): string {
  return file.type
    || (isPdfFile(file) ? 'application/pdf' : '')
    || (isExcelFile(file)
      ? (String(file?.name || '').toLowerCase().endsWith('.xls')
        ? 'application/vnd.ms-excel'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      : '')
    || (isJsonFile(file) ? 'application/json' : 'text/csv');
}

export async function resolveOrbitContextUploadsFromFiles(
  files: FileList | File[],
  options: ResolveOrbitContextUploadsOptions = {},
): Promise<ResolveOrbitContextUploadsResult> {
  const normalizedFiles = Array.from(files || []);
  const uploads: OrbitChatUpload[] = [];
  const errors: string[] = [];
  let storedUploadCount = 0;

  for (const file of normalizedFiles) {
    if (isContextUploadFile(file)) {
      try {
        const upload = await buildOrbitContextUpload(file);
        if (upload) {
          uploads.push(upload);
        } else {
          errors.push(`Unable to read ${file.name} for chat context.`);
        }
      } catch (error: any) {
        errors.push(extractUploadErrorMessage(error, `Unable to read ${file.name} for chat context.`));
      }
      continue;
    }

    if (!isStoredContextUploadFile(file)) {
      errors.push(`${file.name} is not a supported chat context file.`);
      continue;
    }

    try {
      const uploadResponse = await OrbitFilesService.upload(file, {
        clientId: options.clientId,
        conversationId: options.conversationId,
      });
      const orbitFileId = uploadResponse?.data?.id;
      if (!Number.isFinite(orbitFileId)) {
        errors.push(`Unable to store ${file.name} for chat context.`);
        continue;
      }

      const contextResponse = await OrbitFilesService.getContextUpload(orbitFileId);
      if (contextResponse?.status !== 'success' || !contextResponse?.data?.text) {
        errors.push(contextResponse?.message || `Unable to extract reusable chat context from ${file.name}.`);
        continue;
      }

      const storedUpload = contextResponse.data as OrbitChatUpload & { metadata?: Record<string, unknown> };
      uploads.push(normalizeOrbitChatUpload({
        ...storedUpload,
        filename: storedUpload?.filename || file.name,
        content_type: storedUpload?.content_type || fallbackContentTypeForFile(file),
        metadata: {
          ...(storedUpload?.metadata && typeof storedUpload.metadata === 'object' ? storedUpload.metadata : {}),
          filename: storedUpload?.filename || file.name,
          original_filename: file.name,
          content_type: storedUpload?.content_type || fallbackContentTypeForFile(file),
          mime_type: storedUpload?.content_type || fallbackContentTypeForFile(file),
        },
      }));
      storedUploadCount += 1;
    } catch (error: any) {
      errors.push(extractUploadErrorMessage(error, `Unable to add ${file.name} to chat context right now.`));
    }
  }

  return { uploads, storedUploadCount, errors };
}

export function pickOrbitContextFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ORBIT_CONTEXT_FILE_ACCEPT;
    input.multiple = true;

    input.addEventListener('change', () => {
      resolve(Array.from(input.files || []));
    }, { once: true });

    input.click();
  });
}

function uploadSignature(upload: OrbitChatUpload): string {
  const orbitFileId = upload?.metadata && typeof upload.metadata === 'object'
    ? (upload.metadata as Record<string, unknown>).orbit_file_id
    : undefined;
  if (orbitFileId != null) {
    return `orbit-file:${String(orbitFileId)}`;
  }
  return [upload.filename, upload.content_type, upload.text].join('::');
}

export function mergeOrbitContextUploads(
  existingUploads: OrbitChatUpload[],
  incomingUploads: OrbitChatUpload[],
): OrbitChatUpload[] {
  const merged: OrbitChatUpload[] = [];
  const seen = new Set<string>();

  [...existingUploads, ...incomingUploads].forEach((upload) => {
    if (!upload?.filename || !upload?.text) {
      return;
    }
    const signature = uploadSignature(upload);
    if (seen.has(signature)) {
      return;
    }
    seen.add(signature);
    merged.push(upload);
  });

  return merged;
}

export function pickOrbitContextUploads(): Promise<OrbitChatUpload[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.json,text/csv,application/json,text/json,application/ld+json';
    input.multiple = true;

    input.addEventListener('change', async () => {
      try {
        const uploads = await buildOrbitContextUploads(input.files || []);
        resolve(uploads);
      } catch {
        resolve([]);
      }
    }, { once: true });

    input.click();
  });
}

export const pickOrbitCsvUploads = pickOrbitContextUploads;


